/*
  <subscription-panel> — selection state for the PDP subscription panel.

  Deliberately does no pricing. Every figure the panel shows, including the
  Add to Cart label, is rendered by Liquid and read back out of a data
  attribute. That is the whole difference between this and the Recharge
  MutationObserver it replaces in snippets/buy-buttons.liquid, which had to
  scrape prices out of a third-party Vue widget's DOM and broke silently
  whenever Recharge renamed a class.

  What it does own:

    - which rung is selected, and the two form inputs that follow from it
    - which household column is visible
    - the Add to Cart label, copied from the selected rung

  Both household columns are in the DOM already, so switching household is a
  visibility flip rather than a fetch. The hidden column's inputs are kept
  disabled so its variant id can never be submitted.
*/

if (!customElements.get('subscription-panel')) {
  customElements.define(
    'subscription-panel',
    class SubscriptionPanel extends HTMLElement {
      connectedCallback() {
        this.variantInput = this.querySelector('.sp-variant-id');
        this.planInput = this.querySelector('.sp-selling-plan');
        this.ctaLabel = this.querySelector('[data-sp-cta-label]');
        this.submitButton = this.querySelector('.sp-cta');
        this.quantityInput = this.querySelector('[data-sp-quantity]');

        // Nothing to wire on a product with no purchasable rung — an
        // accessory, or a subscription product mid-migration whose plans
        // have not been written yet.
        if (!this.variantInput || !this.planInput || !this.ctaLabel) return;

        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('input', this.onInput.bind(this));

        /*
          THE WELCOME KIT.

          A prepaid rung ships a free kit on its first order. The kit is a
          SEPARATE cart line with no selling plan, because a line without a
          plan is a one-time purchase and never renews — folding it into the
          subscription's own bill of materials would send a tote bag with
          every six-month reorder.

          Dawn's product-form posts a single-item FormData, so the kit cannot
          ride along in the same request. It is added first, in a capture
          listener that stops Dawn's own handler for one pass and then
          re-submits. By the time Dawn adds the subscription and renders the
          cart sections, the kit is already in the cart and appears with it
          rather than popping in afterwards.

          The rules -- which kit belongs to which rung, how many a cart owes --
          live in assets/welcome-kits.js, loaded on every page, so the kit is
          corrected wherever the cart is edited and not only here (#134).
          This panel only tells it which rung is about to be added.
        */
        const form = this.querySelector('form');
        if (form && window.WelcomeKits) form.addEventListener('submit', this.onSubmitCapture.bind(this), true);

        this.evictRechargeWidget();
        this.selectColumn(this.dataset.selectedPeople, { silent: true });
        this.paintFromChecked();
      }

      disconnectedCallback() {
        if (this.rechargeObserver) this.rechargeObserver.disconnect();
        if (this.rechargeTimer) clearTimeout(this.rechargeTimer);
      }

      /* ---------- Recharge ---------- */

      /*
        Recharge's widget mounts into any form that posts to /cart/add, and
        this panel has one. Left alone it injects a second
        `<select name="selling_plan">` INSIDE our form, so the payload carries
        two selling_plan fields — ours and whatever its own UI last had
        selected. They agree on first paint and diverge the moment the
        customer picks a different rung, which is a wrong subscription in the
        cart, not a cosmetic clash.

        Evicting its container takes the stray select with it.

        This is a guard, not the fix. The widget is not a theme app embed —
        none is enabled in config/settings_data.json on staging or production
        (checked 24 Sep 2026). It is Recharge's legacy storefront script,
        static.rechargecdn.com/assets/js/widget.min.js, injected through
        content_for_header, so no theme change can turn it off. The fix is
        disabling the storefront widget in Recharge admin (issue #116). Three
        default-template products (Travel Tin, Storage Tins, Super Focus
        travel pack) still carry Recharge plans and get their subscribe
        option only from that widget, so turning it off is a decision about
        them, not a code change. Until it is off, this keeps the form
        unambiguous. It matches on Recharge's own container class, so if
        Recharge renames it the widget reappears visibly rather than
        corrupting the payload quietly.
      */
      evictRechargeWidget() {
        const evict = () => {
          const found = this.querySelectorAll('.rc-container-wrapper, #recharge-app');
          found.forEach((el) => el.remove());
          return found.length > 0;
        };

        evict();

        // Recharge hydrates after us and can remount. Watch for a bounded
        // window rather than forever — a permanent observer on a subtree this
        // busy is the kind of thing that made PDPs unresponsive before.
        this.rechargeObserver = new MutationObserver(evict);
        this.rechargeObserver.observe(this, { childList: true, subtree: true });
        this.rechargeTimer = setTimeout(() => this.rechargeObserver.disconnect(), 10000);
      }

      /* ---------- reassurance row ---------- */

      /*
        "See lab results" links to this product's certificate of analysis
        file, which works on its own (and without JS). When the page has the
        lab-results drawer (the info_drawer with "Link the certificate of
        analysis" on, marked data-coa-drawer), open it instead, so the shopper
        stays on the PDP and sees the same drawer the Third-Party Lab Results
        button opens. Same on the hero and the single herbs.
      */
      openLabResults(event, link) {
        // ModalDialog moves every drawer to <body> on connect, so look there
        // rather than in this section.
        const drawer = document.querySelector('product-drawer[data-coa-drawer]');
        if (!drawer || typeof drawer.show !== 'function') return;
        event.preventDefault();
        drawer.show(link);
      }

      /* ---------- selection ---------- */

      onClick(event) {
        const coaLink = event.target.closest('[data-sp-coa-link]');
        if (coaLink) {
          this.openLabResults(event, coaLink);
          return;
        }

        const householdButton = event.target.closest('.sp-household__opt');
        if (householdButton) {
          this.selectColumn(householdButton.dataset.spPeople);
          return;
        }

        const option = event.target.closest('[data-sp-option]');
        if (!option || option.hasAttribute('data-sp-unavailable')) return;

        // A click on the <label> already drives the radio, and handling it
        // here as well would toggle twice. Let the change event carry those.
        if (event.target.closest('label, input')) return;

        const radio = option.querySelector('input[type="radio"]');
        if (radio && !radio.disabled) {
          radio.checked = true;
          this.select(option);
        }
      }

      onChange(event) {
        if (event.target.type !== 'radio') return;
        if (this.quantityInput && event.target === this.quantityInput) {
          this.paintCta(this.selectedOption || this.checkedOption());
          return;
        }

        const option = event.target.closest('[data-sp-option]');
        if (option) this.select(option);
      }

      /*
        The kit the selected rung will earn, or null, sized by the panel
        quantity -- welcome-kits.js decides, from the variant's own option
        values, exactly as it will once the rung is in the cart.
      */
      kitForSelection() {
        const option = this.selectedOption || this.checkedOption();
        if (!option || !window.WelcomeKits) return null;
        // One-time rows carry an empty plan, and a one-time line earns no kit.
        if (!option.dataset.spPlan) return null;
        const column = option.closest('[data-sp-column]');
        const values = [option.dataset.spSupply, column && column.dataset.spPeople].filter(Boolean);
        const kit = window.WelcomeKits.kitFor(values);
        if (!kit) return null;
        return { supply: kit.supply, quantity: kit.quantity * this.quantity() };
      }

      async onSubmitCapture(event) {
        const form = event.currentTarget;
        if (form.dataset.spKitAdded === '1') return; // second pass: let Dawn run

        event.preventDefault();
        event.stopImmediatePropagation();

        // Feedback now, not after the kit pass: Dawn only shows its spinner once
        // its own handler runs, which left ~0.6s of nothing after a tap (#200).
        // Dawn sets the same state again and clears it when the add finishes.
        const submitButton = form.querySelector('[type="submit"]');
        const spinner = submitButton && submitButton.querySelector('.loading__spinner');
        // Spinner only: Dawn's onSubmitHandler returns early when the button is
        // aria-disabled, so setting it here would cancel the add itself.
        if (submitButton) submitButton.classList.add('loading');
        if (spinner) spinner.classList.remove('hidden');
        const clearLoading = () => {
          if (submitButton) submitButton.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');
        };

        // Nothing to refresh afterwards: Dawn's own submit is about to add the
        // subscription and render the cart sections, and the kit is already in
        // the cart by then -- which is the whole reason it goes on first. The
        // cart-update Dawn publishes after that add is reconciled by
        // welcome-kits.js like any other, and finds the kit already right.
        try {
          await window.WelcomeKits.reconcile(this.kitForSelection());
          // After the kit, not alongside it: two concurrent writes to one cart
          // can drop one of them.
          await this.recordOffer();
        } catch (error) {
          clearLoading();
          throw error;
        }

        form.dataset.spKitAdded = '1';
        form.requestSubmit();
        delete form.dataset.spKitAdded;
      }

      /*
        AD-OFFER MEASUREMENT (#182).

        When the inline preselect in subscription-panel.liquid fired, write
        what was preselected and what is actually being added onto the cart
        as attributes. They land on the order as note attributes, so an order
        whose _offer_added differs from _offer_preselected is a visitor who
        stepped down on the PDP, and one whose line differs from _offer_added
        stepped down in the cart. Underscored so they stay out of the
        customer's view.

        Measurement must never cost a sale: any failure is swallowed and the
        add to cart goes ahead.
      */
      async recordOffer() {
        const campaign = this.dataset.spOfferActive;
        const option = this.selectedOption || this.checkedOption();
        if (!campaign || !option || !window.routes) return;

        // The one-time row carries no plan and no supply value of its own.
        const added = option.dataset.spPlan ? option.dataset.spSupply || option.dataset.spOption : 'One-time';
        try {
          await fetch(window.routes.cart_update_url, {
            ...fetchConfig(),
            body: JSON.stringify({
              attributes: {
                _offer_campaign: campaign,
                _offer_preselected: this.dataset.spOfferSupply || '',
                _offer_added: added,
              },
            }),
          });
        } catch (error) {
          console.warn('[subscription-panel] offer attributes not recorded', error);
        }
      }

      // `change` on a number input waits for blur, which leaves the CTA
      // stating a price the customer has already stopped believing.
      onInput(event) {
        if (!this.quantityInput || event.target !== this.quantityInput) return;
        this.paintCta(this.selectedOption || this.checkedOption());
      }

      checkedOption() {
        const checked = this.querySelector('[data-sp-column]:not([hidden]) input[type="radio"]:checked');
        return checked ? checked.closest('[data-sp-option]') : null;
      }

      select(option) {
        const column = option.closest('[data-sp-column]');
        if (!column || column.hidden) return;

        this.querySelectorAll('[data-sp-option]').forEach((el) => {
          el.classList.toggle('is-selected', el === option);
        });

        this.variantInput.value = option.dataset.spVariant || '';

        /*
          An empty selling_plan is not the same as an absent one — Shopify
          rejects the line rather than reading it as a one-off purchase. So
          the input is disabled, not blanked, when the one-time row is picked.
        */
        const planId = option.dataset.spPlan || '';
        this.planInput.value = planId;
        this.planInput.disabled = planId === '';

        this.selectedOption = option;
        this.paintCta(option);

        if (this.submitButton) {
          this.submitButton.disabled = option.hasAttribute('data-sp-unavailable');
        }
      }

      /*
        THE CTA LABEL AT A QUANTITY.

        The label is composed in Liquid so it states what the checkout will
        actually charge, which also keeps a money formatter out of this file.
        A quantity breaks that: at three, a label reading $35.00 is simply
        untrue.

        So rather than multiplying anything, the quantity is shown as a
        multiplier against the price Liquid already formatted — "3 x $35.00".
        No arithmetic on money, no second source of truth for the price, and
        the strike-through anchor survives because it is inside the same
        fragment.
      */
      paintCta(option) {
        if (!option || !this.ctaLabel) return;

        const quantity = this.quantity();
        const priceHtml = option.dataset.spPriceHtml;

        if (quantity > 1 && priceHtml && this.dataset.spAddLabel) {
          const label = document.createElement('span');
          label.textContent = `${this.dataset.spAddLabel} \u2013 ${quantity} \u00d7 `;
          this.ctaLabel.replaceChildren(label);
          this.ctaLabel.insertAdjacentHTML('beforeend', priceHtml);
          return;
        }

        if (option.dataset.spCtaHtml) {
          this.ctaLabel.innerHTML = option.dataset.spCtaHtml;
        }
      }

      quantity() {
        if (!this.quantityInput) return 1;
        const parsed = parseInt(this.quantityInput.value, 10);
        return Number.isNaN(parsed) || parsed < 1 ? 1 : parsed;
      }

      paintFromChecked() {
        const checked = this.querySelector('[data-sp-column]:not([hidden]) input[type="radio"]:checked');
        const option = checked
          ? checked.closest('[data-sp-option]')
          : this.querySelector('[data-sp-column]:not([hidden]) [data-sp-option]:not([data-sp-unavailable])');

        if (!option) return;
        const radio = option.querySelector('input[type="radio"]');
        if (radio && !radio.disabled) radio.checked = true;
        this.select(option);
      }

      /* ---------- household ---------- */

      selectColumn(peopleValue, { silent = false } = {}) {
        // `.sp-column` scopes this to the plan and one-time columns: the
        // household buttons carry `data-sp-column` too, and must never be hidden.
        const columns = this.querySelectorAll('.sp-column[data-sp-column]');
        if (!columns.length) return;

        /*
          Carry the chosen rung across the switch. Someone who has picked the
          3-month box and then says there are two of them still wants the
          3-month box, not to be dropped back on the default.
        */
        const previous = this.querySelector('[data-sp-option].is-selected');
        const previousOption = previous ? previous.dataset.spOption : null;

        columns.forEach((column) => {
          const isActive = column.dataset.spPeople === peopleValue;
          column.hidden = !isActive;

          // Disabled rather than merely hidden: a hidden-but-enabled radio
          // is still tabbable and still submits.
          column.querySelectorAll('input[type="radio"]').forEach((radio) => {
            const unavailable = radio.closest('[data-sp-option]').hasAttribute('data-sp-unavailable');
            radio.disabled = !isActive || unavailable;
          });
        });

        this.dataset.selectedPeople = peopleValue;

        this.querySelectorAll('.sp-household__opt').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.spPeople === peopleValue));
        });

        if (silent) return;

        /*
          Scoped to the panel, not to the newly shown column: the plan cards
          and the one-time row live in two separate columns either side of
          the CTA, so "the column that is now visible" is two elements, and
          the rung being carried over could be in either.
        */
        const match = previousOption
          ? this.querySelector(
              `[data-sp-column][data-sp-people="${CSS.escape(peopleValue)}"] ` +
                `[data-sp-option="${CSS.escape(previousOption)}"]:not([data-sp-unavailable])`,
            )
          : null;

        if (match) {
          const radio = match.querySelector('input[type="radio"]');
          if (radio && !radio.disabled) radio.checked = true;
          this.select(match);
        } else {
          this.paintFromChecked();
        }
      }
    },
  );
}
